/**
 * AI-JAVADOC: EuStaticNotificationObjectComponent
 * Component: EuStaticNotificationObjectComponent
 * Module: EU (End User)
 * Description: Object detail view for EuStaticNotification with three navigation tabs:
 *              - Vertragspersonen: Child entity table for EuStaticNotificationContractPersonType
 *              - Vertragstypen: Child entity table for EuStaticNotificationContractType
 *              - Versicherte Objekttypen: Child entity table for EuStaticNotificationInsurableObjectType
 */
import {Component, OnChanges, OnDestroy, OnInit, SimpleChanges} from '@angular/core';
import {ObjectBaseComponent} from '../../../../../core/shared/object/mvs-object-base/object-base.component';
import {MvsCoreService} from '../../../../../core/shared/service/mvs-core.service';
import {MvsMessageService} from '../../../../../core/shared/service/message/mvs-message.service';
import {ConfirmationService} from 'primeng/api';
import {ObserverService} from '../../../../../core/shared/object/service/observer.service';
import {NavigationItem} from '../../../../../core/shared/dto/navigation/navigation-item';
import {WidgetData} from '../../../../../core/shared/widget/widget-data';
import {WidgetFactory} from '../../../../../core/shared/widget/service/widget.factory';
import {FilterCriteria} from '../../../../../core/shared/filter/api/filter.criteria';
import {
    WidgetFunctionCallBackCreate,
    WidgetToolbarCreateInterface
} from '../../../../../core/shared/helper/widget-function-call-back-create';

@Component({
    selector: 'mvs-eu-static-notification-object',
    templateUrl: './eu-static-notification-object.component.html',
    standalone: false
})
export class EuStaticNotificationObjectComponent extends ObjectBaseComponent implements OnInit, OnChanges, OnDestroy {

    contractPersonTypeWidget: WidgetData;
    contractTypeWidget: WidgetData;
    insurableObjectTypeWidget: WidgetData;

    navigationItems: NavigationItem[] = [
        {
            label: 'Vertragspersonen',
            action: 'eu.EuStaticNotificationContractPersonType',
            icon: 'fa-regular fa-user',
            tooltip: 'Vertragspersonen',
            toggleable: false,
            default: true
        },
        {
            label: 'Vertragstypen',
            action: 'eu.EuStaticNotificationContractType',
            icon: 'fa-regular fa-file-contract',
            tooltip: 'Vertragstypen',
            toggleable: false,
            default: false
        },
        {
            label: 'Versicherte Objekttypen',
            action: 'eu.EuStaticNotificationInsurableObjectType',
            icon: 'fa-regular fa-shield',
            tooltip: 'Versicherte Objekttypen',
            toggleable: false,
            default: false
        }
    ];

    constructor(
        protected coreService: MvsCoreService,
        protected messageService: MvsMessageService,
        protected confirmationService: ConfirmationService,
        protected observerService: ObserverService
    ) {
        super(coreService, messageService, confirmationService, observerService);
    }

    ngOnInit() {
        super.ngOnInit();
    }

    /**
     * Called when the object (EuStaticNotification) is loaded or refreshed.
     * Emits navigation items and creates child widgets.
     */
    onObjectChanged() {
        if (!this.activeNavigationItems) {
            this.activeNavigationItems = this.navigationItems[0];
        }
        this.setPageTitle(this.dto['mainLabel']);
        this.onNavigationItems.emit(this.navigationItems);
        this.createChildWidgets();
    }

    /**
     * Create child widgets filtered by this static notification.
     */
    private createChildWidgets(): void {
        const staticNotificationId = this.objectIdentifier.objectId;
        const staticNotificationFilter = [
            FilterCriteria.create('e.staticNotificationId', FilterCriteria.cOperatorEqual, staticNotificationId)
        ];
        const createFields: WidgetToolbarCreateInterface[] = [
            {fieldName: 'staticNotificationDtoId', fieldValue: staticNotificationId}
        ];

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Contract person type table widget filtered by static notification
         */
        this.contractPersonTypeWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'eu.static.notification.obj.contract.person.type',
            'Vertragspersonen',
            'eu.EuStaticNotificationContractPersonType',
            staticNotificationFilter
        );
        this.contractPersonTypeWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Contract type table widget filtered by static notification
         */
        this.contractTypeWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'eu.static.notification.obj.contract.type',
            'Vertragstypen',
            'eu.EuStaticNotificationContractType',
            staticNotificationFilter
        );
        this.contractTypeWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);

        /**
         * AI:
         * Status: 'in progress'
         * Type: Widget
         * SubType: WidgetConfig
         * Reason: Insurable object type table widget filtered by static notification
         */
        this.insurableObjectTypeWidget = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            'eu.static.notification.obj.insurable.object.type',
            'Versicherte Objekttypen',
            'eu.EuStaticNotificationInsurableObjectType',
            staticNotificationFilter
        );
        this.insurableObjectTypeWidget.functionCallbacks = WidgetFunctionCallBackCreate.widgetToolbarCreate(createFields);
    }

    ngOnChanges(changes: SimpleChanges) {
        super.ngOnChanges(changes);
        if (!this.initialized) {
            return;
        }
    }

    ngOnDestroy() {
        super.ngOnDestroy();
    }
}
